import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiCube,
  HiDocumentText,
  HiCalculator,
  HiClock,
  HiCheckCircle,
  HiXCircle,
  HiArrowRight,
  HiLocationMarker,
} from 'react-icons/hi';
import { getStatusColor, getStatusInfo } from '../utils/statusConfig';
import { SkeletonCard, SkeletonTable } from './Skeleton';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBoxes: 0,
    totalInvoices: 0,
    totalCosts: 0,
    totalDespesas: 'R$ 0,00',
    boxesByStatus: {},
    invoicesInProgress: [],
    recentBoxes: [],
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Carregar todas as caixas/malas (API paginada) para totais corretos por status
      let boxes = [];
      let totalBoxesCount = 0;
      try {
        const perPage = 500;
        let page = 1;
        const allBoxes = [];
        let lastTotal = 0;
        for (;;) {
          const boxesData = await apiService.getBoxes({
            page,
            per_page: perPage,
          });
          const pageList = normalizeApiData(boxesData);
          if (!Array.isArray(pageList) || pageList.length === 0) break;
          allBoxes.push(...pageList);
          if (typeof boxesData?.total === 'number') lastTotal = boxesData.total;
          const lastPage = boxesData?.last_page ?? boxesData?.lastPage;
          if (lastPage !== undefined && lastPage !== null) {
            if (page >= Number(lastPage)) break;
            page += 1;
            continue;
          }
          if (pageList.length < perPage) break;
          page += 1;
        }
        boxes = allBoxes;
        totalBoxesCount =
          lastTotal > 0 ? lastTotal : allBoxes.length;
      } catch (error) {
        console.error('Erro ao carregar boxes:', error);
      }

      // Carregar invoices da API
      let invoices = [];
      let totalInvoicesCount = 0;
      try {
        const invoicesData = await apiService.getInvoices();
        invoices = normalizeApiData(invoicesData);
        totalInvoicesCount = typeof invoicesData?.total === 'number' ? invoicesData.total : (Array.isArray(invoices) ? invoices.length : 0);
      } catch (error) {
        console.error('Erro ao carregar invoices:', error);
      }

      // Carregar resumo de custos da API
      let totalCosts = 0;
      let totalDespesasFormatted = 'R$ 0,00';
      try {
        const response = await apiService.getCostsSummary();
        const costsSummary = response?.data || response || {};

        // Formatar valor das despesas
        const formatCurrencyValue = (value) => {
          if (!value && value !== 0) return 'R$ 0,00';
          if (typeof value === 'string') {
            // Se já está formatado, retornar como está
            if (value.includes('R$')) return value;
            // Se é número em string, formatar
            const num = parseFloat(value);
            return isNaN(num) ? 'R$ 0,00' : new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(num);
          }
          // Se é número, formatar
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(value);
        };

        const despesas = costsSummary.despesas || costsSummary.totalDespesas || 0;
        totalDespesasFormatted = formatCurrencyValue(despesas);

        // Manter totalCosts como número para cálculos se necessário
        if (typeof despesas === 'string') {
          const numbers = despesas.replace(/[^\d,.-]/g, '').replace(',', '.');
          totalCosts = parseFloat(numbers) || 0;
        } else {
          totalCosts = parseFloat(despesas) || 0;
        }
      } catch (error) {
        console.error('Erro ao carregar resumo de custos:', error);
      }

      const boxesByStatus = {};
      if (Array.isArray(boxes)) {
        boxes.forEach(box => {
          const raw = box.status != null ? String(box.status).trim() : '';
          const status = raw || 'pedido_criado';
          boxesByStatus[status] = (boxesByStatus[status] || 0) + 1;
        });
      }

      const invoicesInProgress = Array.isArray(invoices)
        ? invoices.filter(inv => {
        const status = inv.status || 'pendente';
        return status !== 'pago' && status !== 'cancelado';
          })
        : [];

      const recentBoxes = Array.isArray(boxes)
        ? [...boxes]
            .map(box => ({
              ...box,
              // Mapear cliente.nome para cliente_nome para facilitar o acesso
              cliente_nome: box.cliente?.nome || box.cliente_nome || '',
              // Mapear codigo_pacote para codigo
              codigo: box.codigo_pacote || box.codigo || '',
            }))
        .sort((a, b) => (b.id || 0) - (a.id || 0))
            .slice(0, 5)
        : [];

      setStats({
        totalBoxes: totalBoxesCount,
        totalInvoices: totalInvoicesCount,
        totalCosts,
        totalDespesas: totalDespesasFormatted,
        boxesByStatus,
        invoicesInProgress,
        recentBoxes,
      });

      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      setLoading(false);
    }
  };

  const getStatusCount = statusValue => {
    return stats.boxesByStatus[statusValue] || 0;
  };

  const formatCurrency = value => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
      <div className='page-container'>
        <div className='breadcrumb'>
          <span>Painel</span>
          <span>Dashboard</span>
        </div>
        <div className='page-header'>
          <div>
            <h1>Dashboard</h1>
            <p>Visão geral do sistema</p>
          </div>
        </div>
        <div className='dashboard-cards'>
          <SkeletonCard count={4} />
        </div>
        <div className='card'>
          <SkeletonTable rows={5} columns={4} />
        </div>
      </div>
    );
  }

  return (
    <div className='page-container'>
      <div className='breadcrumb'>
        <span>Painel</span>
        <span>Dashboard</span>
      </div>

      <div className='page-header'>
        <div>
          <h1>Dashboard</h1>
          <p>Visão geral do sistema</p>
        </div>
      </div>

      <div className='dashboard-cards'>
        <div className='dashboard-card' onClick={() => navigate('/boxes')}>
          <div className='card-icon' style={{ backgroundColor: '#3b82f6' }}>
            <HiCube size={32} />
          </div>
          <div className='card-content'>
            <h3>Caixas / Malas</h3>
            <p className='card-value'>{stats.totalBoxes}</p>
            <p className='card-label'>Total cadastrado</p>
          </div>
          <HiArrowRight className='card-arrow' size={20} />
        </div>

        <div className='dashboard-card' onClick={() => navigate('/invoices')}>
          <div className='card-icon' style={{ backgroundColor: '#10b981' }}>
            <HiDocumentText size={32} />
          </div>
          <div className='card-content'>
            <h3>Invoices</h3>
            <p className='card-value'>{stats.totalInvoices}</p>
            <p className='card-label'>Total de faturas</p>
          </div>
          <HiArrowRight className='card-arrow' size={20} />
        </div>

        <div className='dashboard-card' onClick={() => navigate('/costs')}>
          <div className='card-icon' style={{ backgroundColor: '#ef4444' }}>
            <HiCalculator size={32} />
          </div>
          <div className='card-content'>
            <h3>Custos</h3>
            <p className='card-value'>{stats.totalDespesas || formatCurrency(stats.totalCosts)}</p>
            <p className='card-label'>Total de despesas</p>
          </div>
          <HiArrowRight className='card-arrow' size={20} />
        </div>
      </div>

      <div className='dashboard-section'>
        <div className='section-header'>
          <h2>Caixas / Malas Recentes</h2>
          <button className='btn-link' onClick={() => navigate('/boxes')}>
            Ver todas <HiArrowRight size={16} />
          </button>
        </div>
        <div className='recent-boxes-table'>
          {stats.recentBoxes.length === 0 ? (
            <div className='empty-state'>
              <p>Nenhuma caixa/mala cadastrada</p>
            </div>
          ) : (
            <table className='data-table'>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Tipo</th>
                  <th>Cliente</th>
                  <th>Status</th>
                  <th>Origem</th>
                  <th>Destino</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentBoxes.map(box => {
                  const statusInfo = getStatusInfo(
                    box.status || 'pedido_criado'
                  );
                  const statusColor = getStatusColor(
                    box.status || 'pedido_criado'
                  );

                  return (
                    <tr
                      key={box.id}
                      onClick={() => navigate(`/boxes`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <strong>{box.codigo || box.codigo_pacote || 'N/A'}</strong>
                      </td>
                      <td>{box.tipo === 'caixa' ? '📦 Caixa' : '🧳 Mala'}</td>
                      <td>{box.cliente_nome || box.cliente?.nome || 'N/A'}</td>
                      <td>
                        <span
                          className='badge'
                          style={{
                            backgroundColor: `${statusColor}20`,
                            color: statusColor,
                          }}
                        >
                          {statusInfo.label}
                        </span>
                      </td>
                      <td>{box.origem || 'N/A'}</td>
                      <td>{box.destino || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className='dashboard-section' style={{ marginTop: '2rem' }}>
        <div className='section-header'>
          <h2>Resumo por Status</h2>
        </div>
        <div className='status-summary'>
          <div className='summary-item'>
            <HiCheckCircle size={24} color='#10b981' />
            <div>
              <strong>
                {Object.keys(stats.boxesByStatus).reduce((total, status) => {
                  const statusLower = status.toLowerCase();
                  if (
                    statusLower === 'entregue' ||
                    statusLower === 'entrega_confirmada' ||
                    statusLower.includes('entregue') ||
                    statusLower.includes('entrega_confirmada')
                  ) {
                    return total + (stats.boxesByStatus[status] || 0);
                  }
                  return total;
                }, 0)}
              </strong>
              <span>Entregas Confirmadas</span>
            </div>
          </div>
          <div className='summary-item'>
            <HiClock size={24} color='#f59e0b' />
            <div>
              <strong>
                {Object.keys(stats.boxesByStatus).reduce((total, status) => {
                  const statusLower = status.toLowerCase();
                  if (
                    statusLower !== 'entregue' &&
                    statusLower !== 'entrega_confirmada' &&
                    statusLower !== 'pedido_cancelado' &&
                    statusLower !== 'entrega_cancelada' &&
                    statusLower !== 'em_transito'
                  ) {
                    return total + (stats.boxesByStatus[status] || 0);
                  }
                  return total;
                }, 0)}
              </strong>
              <span>Em Processamento</span>
            </div>
          </div>
          <div className='summary-item'>
            <HiLocationMarker size={24} color='#3b82f6' />
            <div>
              <strong>{getStatusCount('em_transito')}</strong>
              <span>Em Trânsito</span>
            </div>
          </div>
          <div className='summary-item'>
            <HiXCircle size={24} color='#ef4444' />
            <div>
              <strong>
                {getStatusCount('pedido_cancelado') +
                  getStatusCount('entrega_cancelada') +
                  getStatusCount('extraviado')}
              </strong>
              <span>Cancelados/Extraviados</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
